// The HELD answer batch for a surface that shows the readiness card whole — the
// issue page's card and its ask pin, and the /chat/:id inline card.
//
// A click on Loom's answer is the user's answer (see `loomAnswer.ts`). If every
// click went out as its own message, every click that landed while the agent was
// working would kill the running turn: the bridge restarts the agent the instant
// a new prompt arrives. N clicks would cost N turns, each on partial context.
// Focus has always held its answers and flushed them as ONE turn once the user
// had worked through what was asked and the agent was idle; this is the same
// idea for a surface that shows every open field at once.
//
// What it holds: answers (a card row by its field, a pin ask by `ask:<id>`) and
// declined asks. When it flushes: the host can flush (socket open, session
// alive), the agent is idle, and either every open item is addressed (DRAINED:
// each of the host's `openKeys` held or already sent, nothing `openElsewhere`)
// or the user has done nothing on the page for `quietMs` — no click, no key —
// counted from their LAST act, not from their last answer: reading the next
// question and typing its answer is still working through the batch.
//
// A busy agent is WAITED ON. Held answers that land mid-turn kill that turn —
// the bridge restarts the agent the instant a new prompt arrives — and a first
// sandbox turn often runs 60–80 s, so the one-minute backstop this used to have
// killed exactly those (live, #247 at 59 s with no card yet, #258 with
// `propose_card` in flight, 2026-09-25). `HOLD_BUSY_BACKSTOP_MS` only bounds a
// busy flag that never clears.
//
// After a flush the keys are SENT until the agent has been busy and gone idle
// again, a card has read the answer (`groundedThroughSeq` at or past the echo's
// ack seq), or `SENT_MAX_MS` has passed — a row can never be stuck with no
// control on it. Which card READ which answer is kept separately and for longer
// (`answeredAt`): a red is offered as Loom's wording only by a card that has
// read this page's answer to that field.
//
// Persisted in sessionStorage per (project, issue), stamped with the session id,
// so a reload keeps the held answers; a different session or a `/reset` drops
// them.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** How long the user may do nothing on the page before held answers go out on
 *  their own, when every open row is on screen at once (a card, a chat's
 *  proposal): the clicks come fast, and the default for a host. */
export const HOLD_QUIET_MS = 8_000;
/** The same, while a pin still has questions to put one at a time: each needs
 *  reading before it can be answered, and 8 s after the first click sent that
 *  answer ALONE on every run (#247, #248, #258). 20 s was still too short live
 *  (#265, 2026-09-25: the next question read, its answer clicked 20 s after the
 *  first, one second late). The bar says "Loom gets it once you're done" —
 *  Focus waits for exactly that with no timer at all; this only bounds a user
 *  who walked away. */
export const HOLD_QUIET_PIN_MS = 60_000;
/** How long held answers wait on an agent that STAYS busy before going out
 *  anyway — a bound on a busy flag that never clears, far past any real turn. */
export const HOLD_BUSY_BACKSTOP_MS = 300_000;
/** User activity is recorded at most this often (typing is many events). */
const ACTIVITY_THROTTLE_MS = 1_000;
/** The longest a key stays "Sent" with nothing to clear it. */
export const SENT_MAX_MS = 180_000;

export interface HeldEntry {
  /** The batch key: a card field's name, or `ask:<askId>`. */
  readonly key: string;
  /** The readiness field it answers, `null` for a field-less ask. */
  readonly field: string | null;
  /** The typed ask it closes, when it is a pin answer. */
  readonly askId?: string;
  readonly kind?: "question" | "concern";
  readonly text: string;
}

export interface HeldDecline {
  readonly askId: string;
  readonly field: string | null;
}

export interface HeldBatch {
  readonly entries: ReadonlyArray<HeldEntry>;
  readonly declined: ReadonlyArray<HeldDecline>;
  /** A composer message that rides the SAME frame (the issue page's rail). */
  readonly freeText?: string;
}

interface Stored {
  readonly sessionId: string;
  readonly entries: ReadonlyArray<HeldEntry>;
  readonly declined: ReadonlyArray<HeldDecline>;
}

const readStored = (key: string, sessionId: string): Stored | null => {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Stored>;
    if (parsed.sessionId !== sessionId) return null;
    const entries = Array.isArray(parsed.entries)
      ? parsed.entries.filter(
          (e): e is HeldEntry =>
            typeof e === "object" &&
            e !== null &&
            typeof e.key === "string" &&
            (e.field === null || typeof e.field === "string") &&
            typeof e.text === "string" &&
            e.text !== "",
        )
      : [];
    const declined = Array.isArray(parsed.declined)
      ? parsed.declined.filter(
          (d): d is HeldDecline =>
            typeof d === "object" &&
            d !== null &&
            typeof d.askId === "string" &&
            (d.field === null || typeof d.field === "string"),
        )
      : [];
    return { sessionId, entries, declined };
  } catch {
    return null;
  }
};

const writeStored = (key: string, value: Stored | null): void => {
  try {
    if (value === null || (value.entries.length === 0 && value.declined.length === 0)) {
      window.sessionStorage.removeItem(key);
      return;
    }
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* best-effort — a lost persist only re-exposes the reload gap */
  }
};

export interface AnswerHold {
  readonly held: ReadonlyMap<string, HeldEntry>;
  readonly declined: ReadonlyMap<string, HeldDecline>;
  /** Keys whose answer went out and that no card or idle has cleared yet. */
  readonly sent: ReadonlySet<string>;
  /** Key → the delivery id of the LAST flush that carried an answer for it. */
  readonly answeredAt: ReadonlyMap<string, string>;
  readonly count: number;
  readonly add: (entry: HeldEntry) => void;
  readonly decline: (d: HeldDecline) => void;
  readonly undo: (key: string) => void;
  /** Send everything held now (plus `freeText`). Returns whether it sent —
   *  false when there was nothing to send or the host refused the batch. */
  readonly flushNow: (freeText?: string) => boolean;
}

export function useAnswerHold({
  storageKey,
  sessionId,
  resetEpoch,
  canFlush,
  agentBusy,
  openKeys,
  openElsewhere,
  groundedThroughSeq,
  ackedIds,
  stale,
  onFlush,
  quietMs = HOLD_QUIET_MS,
}: {
  /** sessionStorage key, or `null` for no persistence. */
  readonly storageKey: string | null;
  readonly sessionId: string | null;
  readonly resetEpoch: number;
  readonly canFlush: boolean;
  readonly agentBusy: boolean;
  /** The keys the user still has open (card rows by field). The batch is
   *  DRAINED once every one is held or already sent. */
  readonly openKeys: ReadonlyArray<string>;
  /** Something else is still open that this hold does not key (an unanswered
   *  pin ask, the design sketch): not drained yet. */
  readonly openElsewhere: boolean;
  /** The latest card's stamp, for the "a card read it" way out of Sent. */
  readonly groundedThroughSeq: number | undefined;
  readonly ackedIds: ReadonlyMap<string, number | null>;
  /** An entry nobody is waiting for any more (the card was applied, the ask it
   *  answers resolved, a card row's field went green — never an ask's field:
   *  a concern is bound to the green field it is about) — dropped before any
   *  flush. */
  readonly stale?: (entry: HeldEntry) => boolean;
  /** Put the batch on the wire; returns its delivery id ("" when none), or
   *  `null` when the host could NOT take it right now (a chat whose socket is
   *  not open): then nothing is marked sent and every answer stays held. */
  readonly onFlush: (batch: HeldBatch) => string | null;
  /** The quiet window (see `HOLD_QUIET_MS`, `HOLD_QUIET_PIN_MS`). */
  readonly quietMs?: number;
}): AnswerHold {
  const [held, setHeld] = useState<ReadonlyMap<string, HeldEntry>>(new Map());
  const [declined, setDeclined] = useState<ReadonlyMap<string, HeldDecline>>(new Map());
  const [sentAt, setSentAt] = useState<
    ReadonlyMap<string, { readonly clientMsgId: string; readonly at: number; readonly busySeen: boolean }>
  >(new Map());
  const [answeredAt, setAnsweredAt] = useState<ReadonlyMap<string, string>>(new Map());
  const [lastAddAt, setLastAddAt] = useState<number | null>(null);
  // Restore exactly once per (storage key, session): before this lands, the
  // write-back below must not wipe what is stored.
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const loadKey =
    storageKey !== null && sessionId !== null ? `${storageKey}|${sessionId}` : null;

  useEffect(() => {
    if (loadKey === null || loadedFor === loadKey) return;
    const stored = storageKey !== null && sessionId !== null ? readStored(storageKey, sessionId) : null;
    setHeld(new Map((stored?.entries ?? []).map((e) => [e.key, e])));
    setDeclined(new Map((stored?.declined ?? []).map((d) => [d.askId, d])));
    setSentAt(new Map());
    setAnsweredAt(new Map());
    setLoadedFor(loadKey);
  }, [loadKey, loadedFor, storageKey, sessionId]);

  useEffect(() => {
    if (storageKey === null || sessionId === null || loadedFor !== loadKey) return;
    writeStored(storageKey, {
      sessionId,
      entries: [...held.values()],
      declined: [...declined.values()],
    });
  }, [held, declined, storageKey, sessionId, loadedFor, loadKey]);

  // `/reset` rebuilds the conversation under the same id: nothing held belongs
  // to it. Skips the mount firing — a mount is not a reset.
  const epochRef = useRef(resetEpoch);
  useEffect(() => {
    if (epochRef.current === resetEpoch) return;
    epochRef.current = resetEpoch;
    setHeld(new Map());
    setDeclined(new Map());
    setSentAt(new Map());
    setAnsweredAt(new Map());
    if (storageKey !== null) writeStored(storageKey, null);
  }, [resetEpoch, storageKey]);

  // Drop what nobody is waiting for any more — before any flush can carry it.
  useEffect(() => {
    if (stale === undefined || held.size === 0) return;
    const drop = [...held.values()].filter((e) => stale(e));
    if (drop.length === 0) return;
    setHeld((m) => {
      const n = new Map(m);
      for (const e of drop) n.delete(e.key);
      return n;
    });
  });

  const heldRef = useRef(held);
  heldRef.current = held;
  const declinedRef = useRef(declined);
  declinedRef.current = declined;
  const onFlushRef = useRef(onFlush);
  onFlushRef.current = onFlush;
  const staleRef = useRef(stale);
  staleRef.current = stale;

  const flushNow = useCallback((freeText?: string): boolean => {
    const isStale = staleRef.current;
    const entries = [...heldRef.current.values()].filter((e) => !isStale?.(e));
    const declines = [...declinedRef.current.values()];
    if (entries.length === 0 && declines.length === 0 && freeText === undefined) return false;
    const clientMsgId = onFlushRef.current({
      entries,
      declined: declines,
      ...(freeText !== undefined ? { freeText } : {}),
    });
    // Not taken: nothing left the page, so nothing may read as sent — a held
    // answer that vanished here would be a click the user made and Loom never
    // got.
    if (clientMsgId === null) return false;
    const now = Date.now();
    setSentAt((m) => {
      const n = new Map(m);
      for (const e of entries) n.set(e.key, { clientMsgId, at: now, busySeen: false });
      return n;
    });
    setAnsweredAt((m) => {
      const n = new Map(m);
      for (const e of entries) n.set(e.key, clientMsgId);
      return n;
    });
    setHeld(new Map());
    setDeclined(new Map());
    setLastAddAt(null);
    return true;
  }, []);

  const count = held.size + declined.size;
  const drained =
    !openElsewhere && openKeys.every((k) => held.has(k) || sentAt.has(k));

  // The user's last act anywhere on the page while something is held: a key, a
  // pointer, an edit. Throttled — a typed paragraph is hundreds of events.
  const [lastActivityAt, setLastActivityAt] = useState<number | null>(null);
  const lastActivityRef = useRef(0);
  const holding = count > 0;
  useEffect(() => {
    if (!holding) return;
    const onActivity = (): void => {
      const now = Date.now();
      if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
      lastActivityRef.current = now;
      setLastActivityAt(now);
    };
    const kinds = ["keydown", "pointerdown", "input"] as const;
    for (const k of kinds) window.addEventListener(k, onActivity, true);
    return () => {
      for (const k of kinds) window.removeEventListener(k, onActivity, true);
    };
  }, [holding]);

  // THE FLUSH. Idle agent, and either everything open is addressed or the user
  // has been quiet long enough; a busy agent is waited on.
  useEffect(() => {
    if (count === 0 || !canFlush) return;
    if (!agentBusy && drained) {
      flushNow();
      return;
    }
    const added = lastAddAt ?? Date.now();
    const wait = agentBusy
      ? Math.max(0, added + HOLD_BUSY_BACKSTOP_MS - Date.now())
      : Math.max(0, Math.max(added, lastActivityAt ?? 0) + quietMs - Date.now());
    const timer = setTimeout(() => flushNow(), wait);
    return () => clearTimeout(timer);
  }, [count, canFlush, agentBusy, drained, lastAddAt, lastActivityAt, quietMs, flushNow]);

  // The ways out of Sent.
  useEffect(() => {
    if (sentAt.size === 0) return;
    const now = Date.now();
    let changed = false;
    const n = new Map(sentAt);
    for (const [key, rec] of sentAt) {
      const ack = rec.clientMsgId !== "" ? (ackedIds.get(rec.clientMsgId) ?? null) : null;
      const cardRead =
        ack !== null && groundedThroughSeq !== undefined && groundedThroughSeq >= ack;
      if (cardRead || (rec.busySeen && !agentBusy) || now - rec.at >= SENT_MAX_MS) {
        n.delete(key);
        changed = true;
      } else if (agentBusy && !rec.busySeen) {
        n.set(key, { ...rec, busySeen: true });
        changed = true;
      }
    }
    if (changed) setSentAt(n);
  }, [sentAt, agentBusy, ackedIds, groundedThroughSeq]);
  // …and the time bound needs a clock of its own: nothing else re-renders.
  useEffect(() => {
    if (sentAt.size === 0) return;
    const oldest = Math.min(...[...sentAt.values()].map((r) => r.at));
    const timer = setTimeout(
      () => setSentAt((m) => new Map([...m].filter(([, r]) => Date.now() - r.at < SENT_MAX_MS))),
      Math.max(0, oldest + SENT_MAX_MS - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [sentAt]);

  const add = useCallback((entry: HeldEntry) => {
    setHeld((m) => new Map(m).set(entry.key, entry));
    setSentAt((m) => {
      if (!m.has(entry.key)) return m;
      const n = new Map(m);
      n.delete(entry.key);
      return n;
    });
    setLastAddAt(Date.now());
  }, []);
  const decline = useCallback((d: HeldDecline) => {
    setDeclined((m) => new Map(m).set(d.askId, d));
    setLastAddAt(Date.now());
  }, []);
  const undo = useCallback((key: string) => {
    setHeld((m) => {
      if (!m.has(key)) return m;
      const n = new Map(m);
      n.delete(key);
      return n;
    });
    setDeclined((m) => {
      if (!m.has(key)) return m;
      const n = new Map(m);
      n.delete(key);
      return n;
    });
  }, []);

  const sent = useMemo(() => new Set(sentAt.keys()), [sentAt]);
  return { held, declined, sent, answeredAt, count, add, decline, undo, flushNow };
}
